import React, { useCallback, useMemo, useState } from 'react';
import { Box, Stack, TextField, Typography } from '@mui/material';
import { ColorResult, color } from '@uiw/react-color';

import {
  BrandingValue,
  CoBrandingValue,
  ConfigResponse,
} from '../../../store/modules/merchant/types';
import { useMerchant } from '../../../contexts/MerchantContext';
import ColorInput from '../ColorInput';
import { ConfigSection, ParentAwareSaveBar } from '../../../common';
import { InheritedConfigSettingCallouts } from '../../../common/InheritedConfigSettingCallouts';
import { useChildInheritedConfigEdit } from '../../../hooks/useChildInheritedConfigEdit';
import { isConfigInheritedForMerchant } from '../../../util/configInheritance';
import { inheritableLabelsFromConfigNames } from '../../../util/parentSettingSave';
import LogoInput from '../LogoInput';
import ButtonLogoPreview from './ButtonLogoPreview';
import CheckoutLogoPreview from './CheckoutLogoPreview';

function isBrandingColorDirty(
  savedColor: string | undefined,
  currentColor: ColorResult | undefined,
): boolean {
  const normalizedSaved = savedColor?.toLowerCase();
  if (!normalizedSaved) {
    return Boolean(currentColor);
  }
  if (!currentColor) {
    return false;
  }
  const normalizedHex = currentColor.hex.toLowerCase();
  const normalizedHexa = currentColor.hexa.toLowerCase();
  return normalizedHex !== normalizedSaved && normalizedHexa !== normalizedSaved;
}

type Props = {
  branding?: BrandingValue;
  brandingConfig?: ConfigResponse<BrandingValue>;
  cobranding?: CoBrandingValue;
  cobrandingConfig?: ConfigResponse<CoBrandingValue>;
  readOnly?: boolean;
  isSaving?: boolean;
  onSave: (
    branding: BrandingValue,
    coBranding?: CoBrandingValue,
    withFileUploadError?: boolean,
  ) => Promise<void>;
  uploadLogo: (
    file: File,
    type?: 'branding' | 'co-branding',
  ) => Promise<{ logoSrc: string } | void>;
};

export const BrandingForm = ({
  branding = {},
  brandingConfig,
  cobranding = {},
  cobrandingConfig,
  readOnly = false,
  isSaving = false,
  onSave,
  uploadLogo,
}: Props) => {
  const { merchantId, merchantName } = useMerchant();
  const brandingInherited = isConfigInheritedForMerchant(merchantId, brandingConfig);
  const cobrandingInherited =
    cobrandingConfig != null ? isConfigInheritedForMerchant(merchantId, cobrandingConfig) : false;
  const settingInherited = brandingInherited || cobrandingInherited;
  const {
    showInheritedCallouts,
    isCustomizingInherited,
    controlsDisabled,
    onCustomize,
    resetCustomizing,
    getSaveBarInheritanceProps,
    isChildInheritedSaveBarOpen,
    isChildInheritedSaveEnabled,
  } = useChildInheritedConfigEdit(settingInherited, readOnly);
  const defaultColor = branding.color ? color(branding.color) : undefined;
  const [newColor, setNewColor] = useState<ColorResult | undefined>(defaultColor);
  const [newImg, setNewImg] = useState<string | File | null | undefined>(branding.logoSrc);
  const [newButtonImg, setNewButtonImg] = useState<string | File | null | undefined>(
    cobranding.logo,
  );
  const [newName, setNewName] = useState(branding.displayName);
  const [isLogoModified, setIsLogoModified] = useState(false);
  const [isButtonLogoModified, setIsButtonLogoModified] = useState(false);

  const brandingDirtyState = useMemo(() => {
    const isColorModified = isBrandingColorDirty(branding.color, newColor);
    const isNameModified =
      newName != null &&
      newName !== branding.displayName &&
      (branding.displayName || newName !== '');
    const isMerchantBrandingModified = Boolean(isColorModified || isNameModified || isLogoModified);
    const isSaveBarOpen = Boolean(isMerchantBrandingModified || isButtonLogoModified);
    return { isMerchantBrandingModified, isSaveBarOpen };
  }, [
    newColor,
    branding.color,
    branding.displayName,
    newName,
    isLogoModified,
    isButtonLogoModified,
  ]);

  const { isMerchantBrandingModified, isSaveBarOpen } = brandingDirtyState;

  const inheritableSaveBarLabels = useMemo(() => {
    if (!isSaveBarOpen) {
      return [];
    }
    const configNames: ConfigResponse['name'][] = [];
    if (isMerchantBrandingModified) {
      configNames.push('MERCHANT_BRANDING');
    }
    if (isButtonLogoModified) {
      configNames.push('CO_BRANDING');
    }
    return inheritableLabelsFromConfigNames(configNames);
  }, [isButtonLogoModified, isMerchantBrandingModified, isSaveBarOpen]);

  const resetValues = useCallback(() => {
    setNewColor(defaultColor);
    setNewImg(branding.logoSrc);
    setNewName(branding.displayName);
    setNewButtonImg(cobranding.logo);
    setIsLogoModified(false);
    setIsButtonLogoModified(false);
    resetCustomizing();
  }, [branding, cobranding, defaultColor, resetCustomizing]);

  const handleSave = useCallback(async () => {
    let newLogoSrc: string | undefined = typeof newImg === 'string' ? newImg : undefined;
    let newButtonLogoSrc: string | undefined =
      typeof newButtonImg === 'string' ? newButtonImg : undefined;
    let withFileUploadError = false;
    const parsedName = newName?.trim();

    // Branding Data
    const saveData = {
      ...branding,
      color: newColor?.hsla.a === 1 ? newColor?.hex : newColor?.hexa,
      displayName: parsedName,
    };
    if (!parsedName || parsedName === '') {
      delete saveData.displayName;
    }

    if (isLogoModified) {
      if (newImg instanceof File) {
        const response = await uploadLogo(newImg);
        if (!response) {
          withFileUploadError = true;
        }
        newLogoSrc = response?.logoSrc;
      }

      if (newLogoSrc) {
        saveData.logoSrc = `${newLogoSrc}?cache-breaker=${Date.now()}`;
      } else if (newLogoSrc === '') {
        saveData.logoSrc = undefined;
      }
    }

    // Co-branding Data (Logo for button)
    const coBrandingData = isButtonLogoModified
      ? {
          ...cobranding,
        }
      : undefined;

    if (isButtonLogoModified) {
      if (newButtonImg instanceof File) {
        const response = await uploadLogo(newButtonImg, 'co-branding');
        if (!response) {
          withFileUploadError = true;
        }
        newButtonLogoSrc = response?.logoSrc;
      }

      if (newButtonLogoSrc && coBrandingData) {
        coBrandingData.logo = `${newButtonLogoSrc}?cache-breaker=${Date.now()}`;
      } else if (newButtonLogoSrc === '' && coBrandingData) {
        coBrandingData.logo = undefined;
      }
    }

    setNewName(parsedName ?? '');
    setIsLogoModified(false);
    setIsButtonLogoModified(false);
    await onSave(saveData, coBrandingData, withFileUploadError);
    resetCustomizing();
  }, [
    branding,
    newImg,
    newColor,
    newName,
    onSave,
    uploadLogo,
    newButtonImg,
    cobranding,
    isLogoModified,
    isButtonLogoModified,
    resetCustomizing,
  ]);

  const pageInheritableLabels = useMemo(() => {
    const configNames: ConfigResponse['name'][] = [];
    if (brandingInherited) {
      configNames.push('MERCHANT_BRANDING');
    }
    if (cobrandingInherited) {
      configNames.push('CO_BRANDING');
    }
    return inheritableLabelsFromConfigNames(configNames);
  }, [brandingInherited, cobrandingInherited]);
  const showSaveBar = isChildInheritedSaveBarOpen(isSaveBarOpen);
  const saveBarInheritance = getSaveBarInheritanceProps(
    showSaveBar,
    inheritableSaveBarLabels,
    pageInheritableLabels,
  );

  return (
    <>
      {showInheritedCallouts ? (
        <InheritedConfigSettingCallouts
          isCustomizingInherited={isCustomizingInherited}
          readOnly={readOnly}
          onCustomize={onCustomize}
        />
      ) : null}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <ConfigSection
          title="Name and Color"
          description="Your company name will be displayed as it appears below within the Skipify experience. You can also add your brand color and adjust the opacity as fits your needs."
        >
          <Stack spacing={4}>
            <TextField
              label="Display Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={controlsDisabled}
              sx={{ maxWidth: '650px' }}
              helperText={
                newName?.trim() === '' && !!branding.displayName
                  ? `Clearing this field will restore the company’s default name: ${merchantName}`
                  : undefined
              }
            />
            <ColorInput color={newColor} onChange={setNewColor} disabled={controlsDisabled} />
          </Stack>
        </ConfigSection>
        <ConfigSection
          title="Logos"
          description="For best results your file should be on a transparent background. We accept PNG and JPEG formats and should not exceed 148x148 px."
          sx={{ mt: 6 }}
          marginBottom={6}
        >
          <Stack spacing={6}>
            <Stack spacing={3}>
              <Stack spacing={0}>
                <Typography variant="large" fontWeight="bold">
                  Checkout Logo
                </Typography>
                <Typography variant="small" color="text.secondary">
                  This logo will be displayed within the checkout experience.
                </Typography>
              </Stack>
              <Stack direction={{ md: 'row', xs: 'column' }} spacing={8}>
                <LogoInput
                  onDelete={() => {
                    setNewImg('');
                    setIsLogoModified(true);
                  }}
                  img={newImg}
                  onChange={(value) => {
                    setNewImg(value);
                    setIsLogoModified(true);
                  }}
                  disabled={controlsDisabled}
                />
                <CheckoutLogoPreview logo={newImg} />
              </Stack>
            </Stack>
            <Stack spacing={3}>
              <Stack spacing={0}>
                <Typography variant="large" fontWeight="bold">
                  Button Logo
                </Typography>
                <Typography variant="small" color="text.secondary">
                  This logo will be displayed on the Skipify button that is created using the
                  Skipify SDK.
                </Typography>
              </Stack>
              <Stack direction={{ md: 'row', xs: 'column' }} spacing={8}>
                <LogoInput
                  onDelete={() => {
                    setNewButtonImg('');
                    setIsButtonLogoModified(true);
                  }}
                  img={newButtonImg}
                  onChange={(value) => {
                    setNewButtonImg(value);
                    setIsButtonLogoModified(true);
                  }}
                  disabled={controlsDisabled}
                  ariaLabel="Button-Logo"
                />
                <ButtonLogoPreview logo={newButtonImg} />
              </Stack>
            </Stack>
          </Stack>
        </ConfigSection>
      </Box>
      <ParentAwareSaveBar
        disableSave={readOnly || isSaving || !isChildInheritedSaveEnabled(isSaveBarOpen)}
        saveLoading={isSaving}
        show={showSaveBar && !readOnly}
        onCancel={() => resetValues()}
        inheritableSettingLabels={saveBarInheritance.inheritableSettingLabels}
        detailTextOverride={saveBarInheritance.detailTextOverride}
        onSave={handleSave}
      />
    </>
  );
};
